import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiGet } from "../../api/apiclient";
import { AI_AGENTS, PROCESSED_DOCUMENTS, PROCESSED_DOCUMENTS_DETAILS, SEARCH_PROCESSED_DOCUMENTS } from "../../api/endpoints";

// Async thunk for logging in
export const loadAIAgents = createAsyncThunk(
  "/api/ai_agents/",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiGet(AI_AGENTS);
      if (response?.data.status === "success") {
        return response?.data;
      } else {
        return rejectWithValue(
          response.data.message || "Unexpected error occurred."
        );
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message || "Network error occurred.");
    }
  }
);

// Async thunk for processed documents
export const processedDocuments = createAsyncThunk(
  "/api/processed_documents/",
  async (slugname, { rejectWithValue }) => {
    const url = `${PROCESSED_DOCUMENTS}${slugname}/`
    try {
      const response = await apiGet(url);
      console.log('processedDocuments API response:', response);

      if (response?.data.status === "success") {
        console.log('processedDocuments API data:', response?.data?.data)
        // The actual response structure has documents nested under data.documents
        return response?.data?.data?.documents || response?.data?.data;
      } else {
        return rejectWithValue(
          response.data.message || "Unexpected error occurred."
        );
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message || "Network error occurred.");
    }
  }
);

// Async thunk for loading more processed documents (pagination)
export const loadMoreProcessedDocuments = createAsyncThunk(
  "/api/processed_documents/load_more",
  async ({ slugname, page }, { rejectWithValue }) => {
    const url = `${PROCESSED_DOCUMENTS}${slugname}/?page=${page}`;
    try {
      const response = await apiGet(url);
      console.log('loadMoreProcessedDocuments API response:', response);

      if (response?.data.status === "success") {
        console.log('loadMoreProcessedDocuments API data:', response?.data?.data)
        // The actual response structure has documents nested under data.documents
        return response?.data?.data?.documents || response?.data?.data;
      } else {
        return rejectWithValue(
          response.data.message || "Unexpected error occurred."
        );
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message || "Network error occurred.");
    }
  }
);

// Async thunk for logging in
export const processedDocumentsDetailsAPI = createAsyncThunk(
  "/api/processed_documents_details/",
  async (slugname, { rejectWithValue }) => {
    const url = `${PROCESSED_DOCUMENTS_DETAILS}${slugname}/`
    try {
      const response = await apiGet(url);

      if (response?.data.status === "success") {
        console.log('response?.data?.documents', response?.data?.data?.documents)
        return response?.data?.data?.documents;
      } else {
        return rejectWithValue(
          response.data.message || "Unexpected error occurred."
        );
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message || "Network error occurred.");
    }
  }
);

// Async thunk for searching processed documents
export const searchProcessedDocuments = createAsyncThunk(
  "/api/processed_documents/search",
  async ({ slugname, query }, { rejectWithValue }) => {
    const url = `${SEARCH_PROCESSED_DOCUMENTS}${slugname}/?query=${encodeURIComponent(
      query
    )}`;

    try {
      const response = await apiGet(url);

      if (response?.data?.status === "success") {
        return response?.data?.data?.documents || response?.data?.data;
      }

      return rejectWithValue(
        response?.data?.message || "Unexpected error occurred."
      );
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Network error occurred."
      );
    }
  }
);

const LoadAIAgentsSlice = createSlice({
  name: "ai_agents",
  initialState: {
    agentListLoading: false,
    agentListError: {},
    agentList: [],

    processedDocumentsList: [],
    processedDocumentsListLoading: false,
    processedDocumentsListError: {},
    processedDocumentsPagination: {
      count: 0,
      next: null,
      previous: null,
    },

    processedDocumentsDetails: [],
    processedDocumentsDetailsLoading: false,
    processedDocumentsDetailsError: {},

  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAIAgents.pending, (state) => {
        state.agentListLoading = true;
        state.error = null;
      })
      .addCase(loadAIAgents.fulfilled, (state, action) => {
        state.agentListLoading = false;
        state.agentList =
          Array.isArray(action.payload?.data?.agents) &&
            action.payload?.data?.agents.length > 0
            ? action.payload.data.agents
            : state.agentList;
      })
      .addCase(loadAIAgents.rejected, (state, action) => {
        state.agentListLoading = false;
        state.agentListError = action?.payload?.agentListError;
      });

    builder
      .addCase(processedDocuments.pending, (state) => {
        state.processedDocumentsListLoading = true;
        state.processedDocumentsListError = null;
      })
      .addCase(processedDocuments.fulfilled, (state, action) => {
        state.processedDocumentsListLoading = false;
        console.log('processedDocuments.fulfilled - action.payload:', action.payload);
        console.log('processedDocuments.fulfilled - action.payload type:', typeof action.payload);
        console.log('processedDocuments.fulfilled - action.payload.results:', action.payload?.results);

        // Handle the new API response structure with pagination
        if (action.payload?.results && Array.isArray(action.payload.results)) {
          state.processedDocumentsList = action.payload.results;
          state.processedDocumentsPagination = {
            count: action.payload.count || 0,
            next: action.payload.next || null,
            previous: action.payload.previous || null,
          };
        } else if (Array.isArray(action.payload)) {
          // If payload is directly an array (backward compatibility)
          state.processedDocumentsList = action.payload;
          state.processedDocumentsPagination = {
            count: action.payload.length || 0,
            next: null,
            previous: null,
          };
        } else {
          // Fallback for any other structure
          console.warn('Unexpected API response structure:', action.payload);
          state.processedDocumentsList = [];
          state.processedDocumentsPagination = {
            count: 0,
            next: null,
            previous: null,
          };
        }

        console.log('processedDocuments.fulfilled - final state.processedDocumentsList:', state.processedDocumentsList);
      })
      .addCase(processedDocuments.rejected, (state, action) => {
        state.processedDocumentsListLoading = false;
        state.processedDocumentsListError = action.payload;
      });

    // Handle searching processed documents
    builder
      .addCase(searchProcessedDocuments.pending, (state) => {
        state.processedDocumentsListLoading = true;
        state.processedDocumentsListError = null;
      })
      .addCase(searchProcessedDocuments.fulfilled, (state, action) => {
        state.processedDocumentsListLoading = false;

        if (action.payload?.results) {
          state.processedDocumentsList = action.payload.results;
          state.processedDocumentsPagination = {
            count: action.payload.count || 0,
            next: action.payload.next || null,
            previous: action.payload.previous || null,
          };
        } else if (Array.isArray(action.payload)) {
          state.processedDocumentsList = action.payload;
          state.processedDocumentsPagination = {
            count: action.payload.length,
            next: null,
            previous: null,
          };
        }
      })
      .addCase(searchProcessedDocuments.rejected, (state, action) => {
        state.processedDocumentsListLoading = false;
        state.processedDocumentsListError = action.payload;
      });

    // Handle loading more documents (pagination)
    builder
      .addCase(loadMoreProcessedDocuments.pending, (state) => {
        state.processedDocumentsListLoading = true;
        state.processedDocumentsListError = null;
      })
      .addCase(loadMoreProcessedDocuments.fulfilled, (state, action) => {
        state.processedDocumentsListLoading = false;
        console.log('loadMoreProcessedDocuments.fulfilled - action.payload:', action.payload);

        // Append new results to existing list
        if (action.payload?.results && Array.isArray(action.payload.results)) {
          state.processedDocumentsList = [...state.processedDocumentsList, ...action.payload.results];
          state.processedDocumentsPagination = {
            count: action.payload.count || 0,
            next: action.payload.next || null,
            previous: action.payload.previous || null,
          };
        } else if (Array.isArray(action.payload)) {
          // If payload is directly an array (backward compatibility)
          state.processedDocumentsList = [...state.processedDocumentsList, ...action.payload];
          state.processedDocumentsPagination = {
            count: state.processedDocumentsList.length,
            next: null,
            previous: null,
          };
        } else {
          console.warn('Unexpected loadMoreProcessedDocuments API response structure:', action.payload);
        }

        console.log('loadMoreProcessedDocuments.fulfilled - final state.processedDocumentsList:', state.processedDocumentsList);
      })
      .addCase(loadMoreProcessedDocuments.rejected, (state, action) => {
        state.processedDocumentsListLoading = false;
        state.processedDocumentsListError = action.payload;
      });

    builder
      .addCase(processedDocumentsDetailsAPI.pending, (state) => {
        state.processedDocumentsDetailsLoading = true;
        state.processedDocumentsDetailsError = null;
      })
      .addCase(processedDocumentsDetailsAPI.fulfilled, (state, action) => {
        state.processedDocumentsDetailsLoading = false;
        state.processedDocumentsDetails = action.payload
      })
      .addCase(processedDocumentsDetailsAPI.rejected, (state, action) => {
        state.processedDocumentsDetailsLoading = false;
        state.processedDocumentsDetailsError = action.payload;
      });
  },
});

export default LoadAIAgentsSlice.reducer;
